import "./App.css";

import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";

import LoadingState from "./components/ui/LoadingState";
import ProtectedRoute from "./ProtectedRoute";
import { useLocation } from "react-router-dom";
import useTrackPageView from "./hooks/useTrackPageView";

// Route-level code splitting: each page loads in its own chunk on demand, so a
// visitor to the home page no longer downloads video.js, the tiptap editor, the
// puzzle engine, etc. up front. Named-export pages are unwrapped to a default.
const AboutPage = lazy(() => import("./pages/AboutPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const BlackJackPage = lazy(() =>
  import("./pages/games/blackjack/BlackJackPage").then((m) => ({
    default: m.BlackJackPage,
  }))
);
const BlogDetail = lazy(() => import("./components/blogs/BlogDetail"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const CratesPage = lazy(() => import("./pages/crates/CratesPage"));
const FishStream = lazy(() => import("./pages/FishStream"));
const GamesPage = lazy(() => import("./pages/GamesPage"));
const HackerRedirect = lazy(() => import("./pages/HackerRedirect"));
const HomePage = lazy(() => import("./pages/HomePage"));
const MovieDetail = lazy(() => import("./components/movies/MovieDetail"));
const MoviePage = lazy(() => import("./pages/MoviePage"));
const NotFoundPage = lazy(() => import("./pages/404"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PuzzleDetail = lazy(() => import("./components/puzzles/PuzzleDetail"));
const PuzzlesPage = lazy(() => import("./pages/PuzzlesPage"));
const RegistrationPage = lazy(() =>
  import("./pages/RegistrationPage").then((m) => ({
    default: m.RegistrationPage,
  }))
);
const RoulettePage = lazy(() =>
  import("./pages/games/roulette/RoulettePage").then((m) => ({
    default: m.RoulettePage,
  }))
);
const SignInPage = lazy(() =>
  import("./pages/SignInPage").then((m) => ({ default: m.SignInPage }))
);
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const TierListDetailPage = lazy(() => import("./pages/tierlist/TierListDetailPage"));
const TierListsPage = lazy(() => import("./pages/tierlist/TierListsPage"));
const WordlePage = lazy(() => import("./pages/games/wordle"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function TrackPageView() {
  useTrackPageView();
  return null;
}

const App = () => {
  if (process.env.NODE_ENV === "development") {
    console.log("App: Component is rendering");
  }

  return (
    <Router>
      <ScrollToTop />
      <TrackPageView />
      <Suspense fallback={<LoadingState full label="Loading…" />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/hacker" element={<HackerRedirect />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/games" element={<GamesPage />} />
          <Route
            path="/games/wordle"
            element={
              <ProtectedRoute>
                <WordlePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crates"
            element={
              <ProtectedRoute>
                <CratesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/blackjack"
            element={
              <ProtectedRoute>
                <BlackJackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roulette"
            element={
              <ProtectedRoute>
                <RoulettePage />
              </ProtectedRoute>
            }
          />

          <Route path="/fish" element={<FishStream />} />

          <Route path="/puzzles" element={<PuzzlesPage />} />
          <Route path="/puzzles/:id" element={<PuzzleDetail />} />

          <Route path="/about" element={<AboutPage />} />
          <Route path="/blogs" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogDetail />} />

          <Route path="/movies" element={<MoviePage />} />
          <Route path="/movies/:id" element={<MovieDetail />} />

          <Route path="/tier-lists" element={<TierListsPage />} />
          <Route path="/tier-lists/:id" element={<TierListDetailPage />} />

          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
